import { Button } from '@shared/components/ui/button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';
import { useInvoicesPermissions } from '../../hooks/useInvoicesPermissions';
import { faXmark, faCircleCheck, faPenToSquare } from '@fortawesome/pro-light-svg-icons';
import { memo, useCallback } from 'react';

interface SelectionToolbarProps {
  selectedCount: number;
  onClose: () => void;
  onPrefacturar?: () => void;
  onBulkEdit?: () => void;
}

// Componente memoizado para los botones de acción
const ActionButton = memo(({ icon, label, onClick }: { icon: any; label: string; onClick: () => void }) => (
  <Button
    variant="outline"
    size="sm"
    onClick={onClick}
    className="border-card-border bg-transparent"
    data-testid={`selection-action-${label.toLowerCase()}`}
  >
    <FontAwesomeIcon icon={icon} className={`w-3 h-3 mr-2`} />
    {label}
  </Button>
));

ActionButton.displayName = 'ActionButton';

export const SelectionToolbar = memo(({ selectedCount, onClose, onPrefacturar, onBulkEdit }: SelectionToolbarProps) => {
  const { t } = useTranslation('invoicesOrdersModule');

  // 🔒 Verificar permisos con hook específico de Invoices (incluye debug mode)
  const { canCreatePreinvoices, canEditLines } = useInvoicesPermissions();

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  const handlePrefacturar = useCallback(() => {
    if (onPrefacturar) {
      onPrefacturar();
    }
  }, [onPrefacturar]);

  const handleBulkEdit = useCallback(() => {
    if (onBulkEdit) {
      onBulkEdit();
    }
  }, [onBulkEdit]);

  // Si no hay elementos seleccionados, no renderizar nada
  if (selectedCount === 0) {
    return null;
  }

  return (
    <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50">
      <div className="bg-card-bg border border-card-border rounded-lg p-2 shadow-lg">
        <div className="flex items-center gap-4">
          {/* Contador de seleccionados */}
          <div className="flex items-center gap-2">
            <span className="w-6 h-6 bg-primary text-white text-sm rounded-full flex items-center justify-center">
              {selectedCount}
            </span>
            <span className="text-sm font-medium">{t('selection.selected', 'Seleccionados')}</span>
            {/* Botón X para cerrar/limpiar selección */}
            <Button
              variant="ghost"
              size="sm"
              onClick={handleClose}
              className="p-0 hover:bg-transparent ml-1"
              data-testid="clear-selection-button"
            >
              <FontAwesomeIcon icon={faXmark} className="w-4 h-4" />
            </Button>
          </div>

          {/* Botones de acción - Con control de permisos */}
          <div className="flex items-center gap-2">
            {/* 🔒 Botón Editar masivo - Solo visible con permiso de edición */}
            {canEditLines && (
              <ActionButton icon={faPenToSquare} label={t('buttons.bulk_edit', 'Editar')} onClick={handleBulkEdit} />
            )}
            {/* 🔒 Botón Prefacturar - Solo visible con permiso pre-invoices.create */}
            {canCreatePreinvoices && (
              <ActionButton
                icon={faCircleCheck}
                label={t('buttons.prefacturar', 'Prefacturar')}
                onClick={handlePrefacturar}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
});

SelectionToolbar.displayName = 'SelectionToolbar';
